// WebMCP 接口层:让 AI Agent 能直接通过 navigator.modelContext.callTool 游玩小镇
// ⚠️ 所有 callTool 都是异步的,必须 await(见 .well-known/agent-skills/SKILL.md)
import { setToken } from "./api";
import type { api } from "./api";

export type ApiLike = typeof api;

export interface ToolDef {
  name: string;
  description: string;
  inputSchema: any;
}

export const WEBMCP_TOOL_DEFS: ToolDef[] = [
  {
    name: "town_get_state",
    description: "获取小镇当前完整状态:世界(天数/时间/天气)、全部居民(位置/活动/职业)、信息流、我的角色。无需登录。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_residents",
    description: "列出小镇全部居民及其位置、活动、职业、ID。无需登录。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_feed",
    description: "获取小镇信息流(居民日记/目标/高光)。无需登录。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_get_me",
    description: "查看我的登录状态与角色(未登录返回 loggedIn:false)。",
    inputSchema: { type: "object", properties: {} },
  },
  {
    name: "town_guest_join",
    description: "以游客身份一键加入小镇(无需注册):自动创建游客账号与角色,并写入本站登录态。游客身份 24 小时后资料自动清理(留言保留);期间可在留言板留言 2 条。加入后才能移动/对话/设置目标。",
    inputSchema: {
      type: "object",
      properties: { name: { type: "string", description: "可选自定义名字(留空则随机,最长12字)" } },
    },
  },
  {
    name: "town_move",
    description: "移动我的角色到指定网格坐标(x/y 为世界地图坐标)。需先登录。",
    inputSchema: {
      type: "object",
      properties: {
        x: { type: "number", description: "目标 x 坐标" },
        y: { type: "number", description: "目标 y 坐标" },
      },
      required: ["x", "y"],
    },
  },
  {
    name: "town_chat",
    description: "与指定居民对话(agentId 取自 town_get_residents),返回居民回复。需先登录。",
    inputSchema: {
      type: "object",
      properties: {
        agentId: { type: "string", description: "居民 ID" },
        text: { type: "string", description: "想说的话,200字内" },
      },
      required: ["agentId", "text"],
    },
  },
  {
    name: "town_set_goals",
    description: "设置我的长期目标/短期目标/当前指令,角色会据此行动。需先登录。",
    inputSchema: {
      type: "object",
      properties: {
        goalLong: { type: "string", description: "长期目标,100字内" },
        goalShort: { type: "string", description: "短期目标,100字内" },
        command: { type: "string", description: "给角色下达的行动指令,100字内" },
      },
    },
  },
  {
    name: "town_say",
    description: "让我的角色在地图上显示一句说话气泡(60字内)。需先登录。",
    inputSchema: {
      type: "object",
      properties: { text: { type: "string", description: "要显示的话" } },
      required: ["text"],
    },
  },
  {
    name: "town_get_profile",
    description: "查看任意居民的详细档案:性格/背景/心愿/目标/最近记忆。",
    inputSchema: {
      type: "object",
      properties: { agentId: { type: "string", description: "居民 ID" } },
      required: ["agentId"],
    },
  },
];

function requireArgs(args: any, keys: string[]) {
  const missing = keys.filter((k) => args?.[k] === undefined || args?.[k] === null || args?.[k] === "");
  if (missing.length) throw new Error(`缺少必要参数: ${missing.join(", ")}`);
}

const LOGIN_HINT = "请先调用 town_guest_join 以游客身份加入小镇";

async function dispatch(name: string, args: any, api: ApiLike): Promise<any> {
  switch (name) {
    case "town_get_state":
      return api.webmcpState();
    case "town_get_residents":
      return (await api.webmcpState()).residents;
    case "town_get_feed":
      return (await api.webmcpState()).feed;
    case "town_get_me": {
      try {
        return await api.me();
      } catch {
        return { loggedIn: false, hint: LOGIN_HINT };
      }
    }
    case "town_guest_join": {
      const r = await api.guestJoin(String(args.name ?? "").trim());
      setToken(r.token); // 写入本站登录态,后续 town_move/town_chat 等自动携带
      return {
        ok: true,
        isGuest: true,
        username: r.username,
        agent: r.agent,
        note: "已以游客身份进入小镇;24 小时后游客资料将自动清理(留言保留)",
      };
    }
    case "town_move": {
      requireArgs(args, ["x", "y"]);
      return api.webmcpMove(Number(args.x), Number(args.y));
    }
    case "town_chat": {
      requireArgs(args, ["agentId", "text"]);
      return api.chat(String(args.agentId), String(args.text));
    }
    case "town_set_goals": {
      return api.setGoals({
        goalLong: args.goalLong ?? undefined,
        goalShort: args.goalShort ?? undefined,
        command: args.command ?? undefined,
      });
    }
    case "town_say": {
      requireArgs(args, ["text"]);
      return api.webmcpSay(String(args.text));
    }
    case "town_get_profile": {
      requireArgs(args, ["agentId"]);
      return (await api.profile(String(args.agentId))).profile;
    }
    default:
      throw new Error(`未知工具: ${name}`);
  }
}

export async function executeWebmcpTool(
  name: string,
  args: any,
  api: ApiLike
): Promise<{ content: { type: "text"; text: string }[]; isError?: boolean }> {
  try {
    const result = await dispatch(name, args, api);
    return { content: [{ type: "text", text: JSON.stringify(result, null, 2) }] };
  } catch (e: any) {
    return { content: [{ type: "text", text: JSON.stringify({ error: e.message }, null, 2) }], isError: true };
  }
}

/** 注册工具到 navigator.modelContext(mcp-b-global.js 提供) */
// mcp-b-global 的 registerTool 不支持重名覆盖:SPA 内多次挂载 Town 会重复注册并抛
// "Tool already registered" 导致页面崩溃,故同一页面会话只注册一次(幂等)。
let webmcpToolsRegistered = false;
export function registerWebmcpTools(api: ApiLike) {
  const mc: any = (navigator as any).modelContext;
  if (!mc || typeof mc.registerTool !== "function") {
    console.warn("[WebMCP] navigator.modelContext.registerTool 不可用,工具未注册");
    return false;
  }
  if (webmcpToolsRegistered) {
    console.log("[WebMCP] tools already registered, skip");
    return true;
  }
  webmcpToolsRegistered = true;
  for (const def of WEBMCP_TOOL_DEFS) {
    mc.registerTool({
      name: def.name,
      description: def.description,
      inputSchema: def.inputSchema,
      execute: async (args: any) => executeWebmcpTool(def.name, args || {}, api),
    });
  }
  // 方便验收/调试:window.__townWebmcp 直接调用(闭包携带 api)
  (window as any).__townWebmcp = {
    tools: WEBMCP_TOOL_DEFS.map((t) => t.name),
    execute: (name: string, args: any) => executeWebmcpTool(name, args, api),
  };
  console.log("[WebMCP] registered", WEBMCP_TOOL_DEFS.length, "tools");
  return true;
}
